import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import SkeletonGroup from './SkeletonGroup';
import './Skeleton.scss';

/**
 * ListSkeleton component - Use for vertical lists (menus, feeds).
 *
 * This component creates a skeleton layout for vertical lists with configurable
 * number of rows. Each row can optionally include an avatar/icon and text content.
 * It's perfect for loading states of user lists, menu items, comment threads,
 * or any vertical list-based UI.
 *
 * @component
 * @example
 * // Basic list with 5 rows
 * <ListSkeleton rows={5} />
 *
 * @example
 * // List with avatars in each row
 * <ListSkeleton rows={3} showAvatar={true} avatarSize="32px" />
 *
 * @example
 * // List with custom row height and spacing
 * <ListSkeleton
 *   rows={4}
 *   showAvatar={true}
 *   rowHeight="60px"
 *   spacing="xs"
 *   textLines={2}
 * />
 *
 * @example
 * // Menu-style list (no avatars, single text line)
 * <ListSkeleton rows={6} showAvatar={false} textLines={1} />
 */
// Design token defaults (matching _variables.scss)
const LIST_DEFAULTS = {
  avatarSize: '40px', // $skeleton-avatar-size-md
  textLineHeight: '14px', // $skeleton-text-height-md
  nameWidth: '120px', // $skeleton-text-width-name
  borderRadius: '4px', // $skeleton-border-radius-sm
};

const ListSkeleton = ({
  rows = 3,
  showAvatar = true,
  avatarSize = LIST_DEFAULTS.avatarSize,
  rowHeight = 'auto',
  textLines = 2,
  spacing = 'xs',
  className = '',
  ...props
}) => {
  const listRows = Array.from({ length: rows }, (_, index) => {
    const avatar = showAvatar ? (
      <Skeleton
        width={avatarSize}
        height={avatarSize}
        borderRadius='50%'
        className='list-skeleton-avatar'
      />
    ) : null;

    const textContent = (
      <SkeletonGroup direction='vertical' spacing='xs'>
        {Array.from({ length: textLines }, (_, textIndex) => (
          <Skeleton
            key={textIndex}
            width={
              textIndex === 0
                ? LIST_DEFAULTS.nameWidth
                : textIndex === textLines - 1
                ? '70%'
                : '90%'
            }
            height={LIST_DEFAULTS.textLineHeight}
            borderRadius={LIST_DEFAULTS.borderRadius}
            className='list-skeleton-text-line'
          />
        ))}
      </SkeletonGroup>
    );

    return (
      <SkeletonGroup
        key={index}
        direction='horizontal'
        spacing='sm'
        className='list-skeleton-row'
        style={{
          minHeight: rowHeight !== 'auto' ? rowHeight : undefined,
          alignItems: 'center',
        }}
      >
        {avatar}
        {textContent}
      </SkeletonGroup>
    );
  });

  return (
    <SkeletonGroup
      direction='vertical'
      spacing={spacing}
      className={`list-skeleton ${className}`}
      {...props}
    >
      {listRows}
    </SkeletonGroup>
  );
};

ListSkeleton.propTypes = {
  /** Number of list rows to render */
  rows: PropTypes.number,

  /** Whether to show avatar/icon in each row */
  showAvatar: PropTypes.bool,

  /** Size of the avatar/icon */
  avatarSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Height of each list row ('auto' for content-based height) */
  rowHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Number of text lines per row */
  textLines: PropTypes.number,

  /** Spacing between rows (xs, sm, md, lg, xl) */
  spacing: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /** Additional CSS classes */
  className: PropTypes.string,
};

export default ListSkeleton;
